import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowDown, ArrowUp, ArrowUpDown, Eye, Pencil, Trash2 } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DatePicker } from '@/components/ui/DatePicker'
import {
  ListingDataTableCard,
  ListingDataTableScroll,
  LISTING_DATA_TABLE_BODY_CLASS,
  LISTING_DATA_TABLE_CLASS,
  LISTING_DATA_TABLE_HEAD_CLASS,
} from '@/components/ui/listingDataTable'
import companyFormStyles from '@/features/company/styles/companyAdd.module.css'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import { ServiceAgreementEditModal } from '../components/ServiceAgreementEditModal'
import { ChangeReasonModal } from '../components/ChangeReasonModal'
import {
  deleteServiceAgreement,
  getServiceAgreements,
  saveServiceAgreement,
} from '../services/documentManagementService'
import type {
  ServiceAgreementDocument,
  ServiceAgreementSubmitPayload,
} from '../types/document'
import type {
  DocumentManagementListPageProps,
  ServiceAgreementEditableRow,
  ServiceAgreementPendingSave,
  ServiceAgreementRowErrors,
} from '../types'
import {
  isEndDateBeforeStart,
  toDatePickerMinDate,
} from '../utils/effectiveDateRange'
import { END_DATE_BEFORE_START_MESSAGE } from '../constants/effectiveDateRange'

function normalizeDate(value: string | undefined): string {
  if (!value) return ''
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

function endOfYear(isoDate: string): string {
  const match = isoDate.match(/^(\d{4})-\d{2}-\d{2}$/)
  return match ? `${match[1]}-12-31` : ''
}

function makeClientId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function coerceActive(value: ServiceAgreementDocument['is_active']): boolean {
  if (typeof value === 'boolean') return value
  return String(value) === '1' || String(value).toLowerCase() === 'true'
}

function toServiceAgreementEditableRow(doc: ServiceAgreementDocument): ServiceAgreementEditableRow {
  return {
    ...doc,
    client_id: makeClientId(),
    version: doc.version ?? '',
    title: doc.title ?? '',
    content: doc.content ?? '',
    effective_start_date: normalizeDate(doc.effective_start_date),
    effective_end_date: normalizeDate(doc.effective_end_date),
  }
}

function createSnapshot(row: ServiceAgreementEditableRow): Pick<
  ServiceAgreementEditableRow,
  'version' | 'title' | 'content' | 'effective_start_date' | 'effective_end_date' | 'is_active'
> {
  return {
    version: row.version,
    title: row.title,
    content: row.content,
    effective_start_date: row.effective_start_date,
    effective_end_date: row.effective_end_date,
    is_active: row.is_active,
  }
}

function validateRow(row: ServiceAgreementEditableRow): ServiceAgreementRowErrors {
  const errors: ServiceAgreementRowErrors = {}
  if (!String(row.version ?? '').trim()) errors.version = 'Version is required.'
  if (!String(row.title ?? '').trim()) errors.title = 'Document Title is required.'
  if (!row.effective_start_date) errors.effective_start_date = 'Effective start date is required.'
  if (!row.effective_end_date) errors.effective_end_date = 'Effective end date is required.'
  if (isEndDateBeforeStart(row.effective_start_date, row.effective_end_date)) {
    errors.effective_end_date = END_DATE_BEFORE_START_MESSAGE
  }
  return errors
}

function toSubmitPayload(row: ServiceAgreementEditableRow): ServiceAgreementSubmitPayload {
  return {
    title: String(row.title ?? '').trim(),
    version: String(row.version ?? '').trim(),
    content: String(row.content ?? ''),
    effective_start_date: row.effective_start_date,
    effective_end_date: row.effective_end_date,
    is_active: coerceActive(row.is_active),
  }
}

function rowHasPreviewableContent(row: ServiceAgreementEditableRow): boolean {
  const c = row.content != null ? String(row.content).trim() : ''
  const d = row.content_draft != null ? String(row.content_draft).trim() : ''
  return Boolean(c || d)
}

export function DocumentManagementListPage({ embedded = false }: DocumentManagementListPageProps) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<ServiceAgreementEditableRow[]>([])
  const [loading, setLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ServiceAgreementEditableRow | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submittingRowId, setSubmittingRowId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ServiceAgreementEditableRow | null>(null)
  const [errorsByRow, setErrorsByRow] = useState<Record<string, ServiceAgreementRowErrors>>({})

  const [orderBy, setOrderBy] = useState<string>('')
  const [sortBy, setSortBy] = useState<'asc' | 'desc'>('asc')

  const [reasonOpen, setReasonOpen] = useState(false)
  const pendingSaveRef = useRef<ServiceAgreementPendingSave | null>(null)
  const baselineByIdRef = useRef<Record<number, ReturnType<typeof createSnapshot>>>({})
  const mountedRef = useRef(false)

  const load = useCallback(async (targetOrderBy = orderBy, targetSortBy = sortBy) => {
    setLoading(true)
    try {
      const docs = await getServiceAgreements({
        page: 1,
        perPage: 15,
        orderBy: targetOrderBy || undefined,
        sortBy: targetSortBy,
      })
      const editableRows = docs.map(toServiceAgreementEditableRow)
      setRows(editableRows)
      const nextBaseline: Record<number, ReturnType<typeof createSnapshot>> = {}
      editableRows.forEach((row) => {
        if (row.id) nextBaseline[row.id] = createSnapshot(row)
      })
      baselineByIdRef.current = nextBaseline
      setErrorsByRow({})
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [orderBy, sortBy])

  const handleSort = (field: string) => {
    const nextSortBy = sortBy === 'asc' ? 'desc' : 'asc'
    setSortBy(nextSortBy)
    setOrderBy(field)
    void load(field, nextSortBy)
  }

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load()
  }, [load])

  const openEdit = (doc: ServiceAgreementEditableRow) => {
    setEditTarget(doc)
    setEditOpen(true)
  }

  const openEditRouteOrModal = (row: ServiceAgreementEditableRow) => {
    if (row.id) {
      navigate(`/gdpr-consent/edit/${row.id}`)
      return
    }
    openEdit(row)
  }

  const isDirtyExistingRow = (row: ServiceAgreementEditableRow): boolean => {
    if (!row.id) return false
    const baseline = baselineByIdRef.current[row.id]
    if (!baseline) return false
    const current = createSnapshot(row)
    return (
      baseline.version !== current.version ||
      baseline.title !== current.title ||
      baseline.content !== current.content ||
      baseline.effective_start_date !== current.effective_start_date ||
      baseline.effective_end_date !== current.effective_end_date ||
      baseline.is_active !== current.is_active
    )
  }

  const updateRow = (clientId: string, patch: Partial<ServiceAgreementEditableRow>) => {
    setRows((current) =>
      current.map((row) => (row.client_id === clientId ? { ...row, ...patch } : row)),
    )
    setErrorsByRow((current) => {
      if (!current[clientId]) return current
      const next = { ...current }
      delete next[clientId]
      return next
    })
  }

  const performSave = async (payload: ServiceAgreementSubmitPayload, id?: number, clientId?: string) => {
    setSubmittingRowId(clientId ?? null)
    setSubmitting(true)
    try {
      await saveServiceAgreement(payload, id)
      showSuccess(id ? 'Document Record Updated' : 'Document Record Added')
      setEditOpen(false)
      setEditTarget(null)
      setReasonOpen(false)
      pendingSaveRef.current = null
      await load()
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
      setSubmittingRowId(null)
    }
  }

  const handleSave = async (row: ServiceAgreementEditableRow) => {
    const rowErrors = validateRow(row)
    if (Object.keys(rowErrors).length > 0) {
      setErrorsByRow((current) => ({ ...current, [row.client_id]: rowErrors }))
      return
    }
    if (row.id && isDirtyExistingRow(row)) {
      pendingSaveRef.current = { payload: toSubmitPayload(row), id: row.id, clientId: row.client_id }
      setReasonOpen(true)
      return
    }
    await performSave(toSubmitPayload(row), row.id, row.client_id)
  }

  const handleModalSubmit = async (
    payload: ServiceAgreementSubmitPayload,
    id: number | undefined,
    isDirtyEdit: boolean,
  ) => {
    if (isDirtyEdit && id) {
      pendingSaveRef.current = { payload, id }
      setReasonOpen(true)
      return
    }
    await performSave(payload, id)
  }

  const handleReasonSubmit = async (reason: string) => {
    const pending = pendingSaveRef.current
    if (!pending) return
    await performSave(
      { ...pending.payload, change_reason: reason },
      pending.id,
      pending.clientId,
    )
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    if (!deleteTarget.id) {
      setRows((current) => current.filter((row) => row.client_id !== deleteTarget.client_id))
      setDeleteTarget(null)
      return
    }
    try {
      await deleteServiceAgreement(deleteTarget.id)
      showSuccess('Documnet Deleted Successfully!')
      setDeleteTarget(null)
      await load()
    } catch (error) {
      showApiError(error)
    }
  }

  const openPreview = (row: ServiceAgreementEditableRow) => {
    if (!row.id || !rowHasPreviewableContent(row)) return
    window.open(
      `/gdpr-consent/view/${row.id}`,
      '_blank',
      'noopener,noreferrer',
    )
  }

  const addDocumentButton = (
    <button
      type="button"
      onClick={() =>
        setRows((current) => [
          {
            client_id: makeClientId(),
            title: '',
            version: '',
            content: '',
            effective_start_date: '',
            effective_end_date: '',
            is_active: false,
          },
          ...current,
        ])
      }
      className={cn(primaryEntityActionButtonClass, 'px-4 py-2 w-full sm:w-auto')}
    >
      Add
    </button>
  )

  const fieldClassName = companyFormStyles.customFormControl

  const tableSection = (
    <ListingDataTableCard>
      <ListingDataTableScroll>
        <table className={LISTING_DATA_TABLE_CLASS}>
          <thead className={LISTING_DATA_TABLE_HEAD_CLASS}>
            <tr>
              <th
                className="cursor-pointer px-4 py-3 text-left hover:bg-slate-100"
                onClick={() => handleSort('version')}
              >
                <div className="flex items-center gap-1">
                  <span>Version</span>
                  {orderBy === 'version' ? (
                    sortBy === 'asc' ? (
                      <ArrowUp className="h-3.5 w-3.5 text-slate-700" />
                    ) : (
                      <ArrowDown className="h-3.5 w-3.5 text-slate-700" />
                    )
                  ) : (
                    <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
                  )}
                </div>
              </th>
              <th
                className="cursor-pointer px-4 py-3 text-left hover:bg-slate-100"
                onClick={() => handleSort('title')}
              >
                <div className="flex items-center gap-1">
                  <span>Document Name</span>
                  {orderBy === 'title' ? (
                    sortBy === 'asc' ? (
                      <ArrowUp className="h-3.5 w-3.5 text-slate-700" />
                    ) : (
                      <ArrowDown className="h-3.5 w-3.5 text-slate-700" />
                    )
                  ) : (
                    <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-left">Effective Dates</th>
              <th className="px-4 py-3 text-left">Action</th>
            </tr>
          </thead>
        <tbody className={LISTING_DATA_TABLE_BODY_CLASS}>
          {loading ? (
              <ListingTableLoadingRow colSpan={4} />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                No service agreements yet.
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const active = coerceActive(row.is_active)
              const rowErrors = errorsByRow[row.client_id] ?? {}
              return (
                <tr key={row.client_id} className="align-top">
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={row.version}
                      onChange={(event) => updateRow(row.client_id, { version: event.target.value })}
                      placeholder="Version"
                      className={fieldClassName}
                    />
                    <div className="mt-1 flex items-center gap-2">
                      {active ? (
                        <span className="text-[11px] font-medium text-slate-600">(active)</span>
                      ) : null}
                      {rowErrors.version ? (
                        <span className="text-xs text-red-600">{rowErrors.version}</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={row.title}
                      onChange={(event) => updateRow(row.client_id, { title: event.target.value })}
                      placeholder="Document Title"
                      className={fieldClassName}
                    />
                    {rowErrors.title ? (
                      <p className="mt-1 text-xs text-red-600">{rowErrors.title}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-2">
                      <div className="min-w-[180px]">
                        <DatePicker
                          value={row.effective_start_date}
                          onChange={(value) => {
                            const nextEnd = value ? endOfYear(value) : row.effective_end_date
                            updateRow(row.client_id, {
                              effective_start_date: value,
                              effective_end_date: nextEnd,
                            })
                          }}
                          placeholder="Start Date"
                          allowFutureDates={true}
                          showHelperText={false}
                          className={fieldClassName}
                        />
                        {rowErrors.effective_start_date ? (
                          <p className="mt-1 text-xs text-red-600">{rowErrors.effective_start_date}</p>
                        ) : null}
                      </div>
                      <span className="pt-2 text-slate-600">To</span>
                      <div className="min-w-[180px]">
                        <DatePicker
                          value={row.effective_end_date}
                          onChange={(value) => updateRow(row.client_id, { effective_end_date: value })}
                          placeholder="End Date"
                          allowFutureDates={true}
                          showHelperText={false}
                          className={fieldClassName}
                          minDate={toDatePickerMinDate(row.effective_start_date)}
                        />
                        {rowErrors.effective_end_date ? (
                          <p className="mt-1 text-xs text-red-600">{rowErrors.effective_end_date}</p>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => void handleSave(row)}
                          disabled={submittingRowId === row.client_id}
                          className={cn(primaryEntityActionButtonClass, 'px-2 py-1 text-xs')}
                        >
                          {submittingRowId === row.client_id ? 'Saving...' : 'Save'}
                        </button>
                        {row.id && rowHasPreviewableContent(row) ? (
                          <button
                            type="button"
                            onClick={() => openPreview(row)}
                            className={cn(secondaryEntityActionButtonClass, 'px-2 py-1 text-xs text-slate-700')}
                            title="Preview"
                            aria-label="Preview"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => openEditRouteOrModal(row)}
                          className={cn(secondaryEntityActionButtonClass, 'px-2 py-1 text-xs text-[#1597ff] hover:text-[#0d7acc]')}
                          title={row.id ? 'Edit Content' : 'Edit Details & Content'}
                          aria-label={row.id ? 'Edit Content' : 'Edit Details & Content'}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {!row.id || !active ? (
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(row)}
                            className={cn(secondaryEntityActionButtonClass, 'px-2 py-1 text-xs text-red-600 hover:text-red-700')}
                            title="Delete"
                            aria-label="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                      </div>
                    {rowErrors.content ? (
                      <p className="mt-1 text-xs text-red-600">{rowErrors.content}</p>
                    ) : null}
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
      </ListingDataTableScroll>
    </ListingDataTableCard>
  )

  const inner = (
    <div className="space-y-4">
      {embedded ? <div className="flex flex-wrap items-center justify-end gap-2">{addDocumentButton}</div> : null}
      {tableSection}

      <ServiceAgreementEditModal
        isOpen={editOpen}
        document={editTarget}
        submitting={submitting && !reasonOpen}
        onClose={() => {
          setEditOpen(false)
          setEditTarget(null)
        }}
        onSubmit={handleModalSubmit}
      />

      <ChangeReasonModal
        isOpen={reasonOpen}
        submitting={submitting}
        onClose={() => {
          setReasonOpen(false)
          pendingSaveRef.current = null
        }}
        onSubmit={handleReasonSubmit}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Delete Service Agreement"
        message="Do You Want To Delete this Service Agreement?"
        primaryLabel="Delete"
        onPrimary={confirmDelete}
        secondaryLabel="Cancel"
        onSecondary={() => setDeleteTarget(null)}
        variant="warning"
      />
    </div>
  )

  if (embedded) {
    return inner
  }

  return (
    <PageShell title="SERVICE AGREEMENT" headerAction={addDocumentButton}>
      {inner}
    </PageShell>
  )
}
